import { Platform } from 'react-native';
import * as Location from 'expo-location';

import type { RouteGeocodeCacheStorage, RoutePlanStop } from '@/types/route-plan';
import { ScopedAsyncStorage } from '@/lib/storage';

const CACHE_STORAGE_KEY = '@route_geocode_cache';
const STORAGE_VERSION = '1.0';

/**
 * Turns free-text stop locations into coordinates using the device geocoder
 * (expo-location). Results are cached in scoped AsyncStorage keyed by
 * normalized location text. Stops whose text cannot be geocoded are returned
 * unchanged — they are flagged by the map UI, never guessed.
 */
export class RouteGeocoder {
  private readonly storage: ScopedAsyncStorage;

  constructor(storage: ScopedAsyncStorage) {
    this.storage = storage;
  }

  private normalizeLocation(location: string): string {
    return location.trim().toLowerCase();
  }

  private async loadCache(): Promise<RouteGeocodeCacheStorage> {
    try {
      const raw = await this.storage.getItem(CACHE_STORAGE_KEY);
      if (!raw) {
        return { coordsByLocation: {}, version: STORAGE_VERSION };
      }
      return JSON.parse(raw) as RouteGeocodeCacheStorage;
    } catch (error) {
      console.error('[RouteGeocoder] Error loading cache:', error);
      return { coordsByLocation: {}, version: STORAGE_VERSION };
    }
  }

  /**
   * Android requires a foreground location permission for geocodeAsync;
   * iOS CLGeocoder does not. A denial means "no coordinates", never a block.
   */
  private async ensureAndroidPermission(): Promise<boolean> {
    if (Platform.OS !== 'android') return true;
    try {
      const current = await Location.getForegroundPermissionsAsync();
      if (current.granted) return true;
      const requested = await Location.requestForegroundPermissionsAsync();
      return requested.granted;
    } catch (error) {
      console.error('[RouteGeocoder] Permission check failed:', error);
      return false;
    }
  }

  /**
   * Returns a new stops array with latitude/longitude attached wherever the
   * location text resolves (cache first, then device geocoder). Never throws.
   */
  async geocodeStops(stops: RoutePlanStop[]): Promise<RoutePlanStop[]> {
    if (Platform.OS === 'web') return stops;

    const pending = stops.filter(
      (stop) => stop.location?.trim() && stop.latitude === undefined,
    );
    if (pending.length === 0) return stops;
    if (!(await this.ensureAndroidPermission())) return stops;

    const cache = await this.loadCache();
    const coordsByKey = new Map<string, { latitude: number; longitude: number }>();
    let cacheDirty = false;

    for (const stop of pending) {
      const key = this.normalizeLocation(stop.location as string);
      if (coordsByKey.has(key)) continue;

      const cached = cache.coordsByLocation[key];
      if (cached) {
        coordsByKey.set(key, cached);
        continue;
      }

      try {
        const results = await Location.geocodeAsync(stop.location as string);
        if (results.length > 0) {
          const coords = { latitude: results[0].latitude, longitude: results[0].longitude };
          coordsByKey.set(key, coords);
          cache.coordsByLocation[key] = coords;
          cacheDirty = true;
        }
      } catch (error) {
        console.error('[RouteGeocoder] Geocode failed for a stop:', error);
      }
    }

    if (cacheDirty) {
      await this.storage.setItem(CACHE_STORAGE_KEY, JSON.stringify(cache));
    }

    return stops.map((stop) => {
      if (!stop.location?.trim() || stop.latitude !== undefined) return stop;
      const coords = coordsByKey.get(this.normalizeLocation(stop.location));
      return coords ? { ...stop, ...coords } : stop;
    });
  }
}
